import {
  Button,
  Code,
  Divider,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Skeleton,
  Spacer,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { Form, Formik, FormikProps } from 'formik';
import * as _ from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';

import {
  borrowingBasePipeline as bbpl,
  consts as commonConsts,
  date as commonDate,
  finleyAssert,
  finleyAssertUnreachable,
  typeguards as commonTypeguards,
  utils as commonUtils,
} from '@finley-technologies/common';
import { CreditAgreement, Util } from '@finley-technologies/finley-types';

import {
  DeliverableArtifactGQLType,
  DeliverableRawNamespaceGQLType,
} from 'types/dashboard-core-gqlTypes.generated.d';

import { DateVersionPicker, RDFV } from './DateVersionPicker';
import { getErrorMessageFromApolloError } from '~/contexts/ApolloClientProvider';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import { useCreateDeliverableArtifactInstanceMutation } from '~/core/routes/deliverables/[deliverableId]/mutations/CreateDeliverableArtifactInstance.gql.generated';
import { GetDeliverableArtifactsByContainerIdDocumentGQLType } from '~/core/routes/deliverables/[deliverableId]/queries/GetDeliverableArtifactsByContainerId.gql.generated';
import { GetDeliverableContainerByIdDocumentGQLType } from '~/core/routes/deliverables/[deliverableId]/queries/GetDeliverableContainerById.gql.generated';
import { colors } from '~/packages/common-frontend/colors';
import { SimpleDatePicker } from '~/packages/common-frontend/components/SimpleDatePicker';
import { errorToastConfig } from '~/packages/common-frontend/constants';
import { getUnexpectedErrorMessage } from '~/packages/common-frontend/utils';
import { useProduceFinancialCovenantWorkleaseMessageMutation } from '~/packages/covenants/requests/financialCovenants.gql.generated';
import {
  useEvaluatePortfolioCovenantsMutation,
  useGetCurrentCreditAgreementFullArtifactQuery,
} from '~/packages/creditAgreements/requests/requests.gql.generated';
import { parseCreditAgreementFullArtifact } from '~/packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { useGetBorrowerPipelineRunsQuery } from '~/packages/dataPipeline/requests/dataPipeline.gql.generated';
import {
  DataTape,
  DataTapeState,
} from '~/packages/manualDataTapeUploads/types';
import { getTitleCaseExternalFilename } from '~/packages/manualDataTapeUploads/ui/DataTapeContainer';
import { UploadCashBalanceForm } from '~/packages/manualDataTapeUploads/ui/UploadCashBalanceForm';
import { UploadDataTapeForm } from '~/packages/manualDataTapeUploads/ui/UploadDataTapeForm';
import { useGetOrgProvidedInputByKeyQuery } from '~/packages/orgProvidedInputs/requests/requests.gql.generated';
import {
  ValueCreateForm,
  ValueCreateModal,
} from '~/packages/orgProvidedInputs/ui/ValueCreateModal';
import { useProduceReportingGenerateMessageMutation } from '~/packages/reporting/requests/reporting.gql.generated';

// Fragments
type ErrorModalProps = {
  text: string;
  isOpen: boolean;
  onClose: () => void;
};
const ErrorModal = ({ text, isOpen, onClose }: ErrorModalProps) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent padding="0px" pt="8px" width="auto" minW="850px">
        <ModalHeader fontSize="xl">Contact Finley Support.</ModalHeader>
        <ModalCloseButton _focus={{ boxShadow: 'none' }} />
        <ModalBody mb="12px">
          <Text>{text}</Text>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

type DataTapeModalProps = {
  artifact: DeliverableArtifactGQLType;
  isOpen: boolean;
  onClose: () => void;
  dataTape?: DataTapeState;
  onSubmitDataTape: (
    file: File,
    tape: DataTape,
    uploadReportingDate: Util.FullDateStr,
    fileVersion: number,
  ) => Promise<void>;
  /** If true, default to new version; otherwise default to highest available existing version */
  defaultToNewVersion: boolean;
};
const DataTapeModal = ({
  artifact,
  isOpen,
  onClose,
  dataTape,
  onSubmitDataTape,
  defaultToNewVersion,
}: DataTapeModalProps) => {
  const { user } = useUserAuthOrThrow();

  // get the minimum date you can upload for using reporting date + reporting period
  const { start: minDateStr, end: maxDateStr } = commonDate.getSearchRange({
    reportingDate: artifact.reportingDate,
    reportingPeriod: JSON.parse(artifact.reportingPeriod),
  });

  const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const maxCurrentDate = commonDate.fullDateStrToDate(
    commonDate.addBusinessDays(
      commonDate.dateToFullDateString(new Date(), clientTimeZone),
      commonConsts.maxFutureBusinessDaysReportingDate,
    ),
  );
  const maxDate = commonDate.fullDateStrToDate(maxDateStr);
  // datePickerMaxDate is the minimum of `maxCurrentDate` and `maxDate`
  const datePickerMaxDate = maxCurrentDate < maxDate ? maxCurrentDate : maxDate;
  const datePickerMinDate = commonDate.fullDateStrToDate(minDateStr);

  const reportingDateAsDate = commonDate.fullDateStrToDate(
    artifact.reportingDate,
  );
  const initialDate =
    reportingDateAsDate <= datePickerMaxDate &&
    reportingDateAsDate >= datePickerMinDate
      ? artifact.reportingDate
      : minDateStr;

  // Track selected date and version from the picker
  const [selectedRDFV, setSelectedRDFV] = useState<RDFV>({
    reportingDate: initialDate,
    fileVersion: 1,
  });

  const handleRDFVSelect = useCallback((rdfv: RDFV) => {
    setSelectedRDFV(rdfv);
  }, []);

  // if minDate > maxDate then we show an error modal & do not proceed with upload
  if (!bbpl.isReportDateLessThanMaxFutureBusinessDays(minDateStr)) {
    const daysToSubtract = -1 * commonConsts.maxFutureBusinessDaysReportingDate;
    const validDate = commonDate.addBusinessDays(minDateStr, daysToSubtract);
    return (
      <ErrorModal
        text={`You cannot upload this file until ${commonDate.fullDateStrToHumanDateString(
          validDate,
        )}.`}
        isOpen={isOpen}
        onClose={onClose}
      />
    );
  }

  if (!dataTape) {
    // Means either loanProductSlug or bbplArtifactSlug does not exist in tape builder
    return (
      <ErrorModal
        text="Missing data tape. Contact Finley Support."
        isOpen={isOpen}
        onClose={onClose}
      />
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent padding="0px" pt="8px" width="auto" minW="850px">
        <ModalHeader fontSize="2xl">
          {`Upload ${getTitleCaseExternalFilename(dataTape.externalFilename)}`}
        </ModalHeader>
        <ModalCloseButton _focus={{ boxShadow: 'none' }} />
        <ModalBody>
          <DateVersionPicker
            organizationId={user.organization.id}
            minDate={datePickerMinDate}
            maxDate={datePickerMaxDate}
            initialDate={initialDate}
            artifactId={dataTape.artifactId}
            defaultToNewVersion={defaultToNewVersion}
            onSelect={handleRDFVSelect}
          />
          <UploadDataTapeForm
            dataTape={dataTape}
            allowedExtensions={[dataTape.fileType]}
            onClose={onClose}
            handleFile={(file) => {
              onSubmitDataTape(
                file,
                dataTape,
                selectedRDFV.reportingDate,
                selectedRDFV.fileVersion,
              );
            }}
          />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

type CashBalancesFeedModalProps = {
  creditFacilityName: string;
  creditFacilityId: string;
  organizationId: string;
  artifact: DeliverableArtifactGQLType;
  isOpen: boolean;
  onClose: () => void;
};
const CashBalancesFeedModal = ({
  creditFacilityName,
  creditFacilityId,
  organizationId,
  artifact,
  isOpen,
  onClose,
}: CashBalancesFeedModalProps) => {
  const toast = useToast();
  const { user } = useUserAuthOrThrow();
  finleyAssert(
    artifact.config.__typename === 'CashBalancesFeedConfig',
    `Deliverables: Cannot evaluate report for deliverable artifact type ${artifact.config.artifactType}. Must be cashBalancesFeed type.`,
  );

  // Fetch pipeline runs to determine the next file version
  const {
    data: pipelineRunsData,
    loading: pipelineRunsLoading,
  } = useGetBorrowerPipelineRunsQuery({
    variables: {
      borrowerOrganizationId: user.organization.id,
      startDate: artifact.reportingDate,
      endDate: artifact.reportingDate,
    },
    fetchPolicy: 'network-only',
  });

  // Calculate fileVersion: find existing run for this date and increment, or default to 1
  const fileVersion = useMemo(() => {
    const existingRun = pipelineRunsData?.getPipelineRuns?.find(
      (run) => run.reportingDate === artifact.reportingDate,
    );
    return existingRun ? existingRun.fileVersion + 1 : 1;
  }, [pipelineRunsData, artifact.reportingDate]);

  const [
    createDeliverableArtifactInstanceMutation,
  ] = useCreateDeliverableArtifactInstanceMutation({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: GetDeliverableArtifactsByContainerIdDocumentGQLType,
        fetchPolicy: 'network-only',
        variables: { containerId: artifact.containerId },
      },
      {
        query: GetDeliverableContainerByIdDocumentGQLType,
        fetchPolicy: 'network-only',
        variables: { containerId: artifact.containerId },
      },
    ],
  });

  if (pipelineRunsLoading) {
    return (
      <Modal
        closeOnOverlayClick={false}
        isOpen={isOpen}
        onClose={onClose}
        size="lg"
      >
        <ModalOverlay />
        <ModalContent padding="0px" pt="8px" width="auto" minW="850px">
          <ModalHeader fontSize="2xl">Upload Cash Balance</ModalHeader>
          <ModalCloseButton _focus={{ boxShadow: 'none' }} />
          <ModalBody>
            <Skeleton height="200px" />
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  }

  return (
    <Modal
      closeOnOverlayClick={false}
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
    >
      <ModalOverlay />
      <ModalContent padding="0px" pt="8px" width="auto" minW="850px">
        <ModalHeader fontSize="2xl">Upload Cash Balance</ModalHeader>
        <ModalCloseButton _focus={{ boxShadow: 'none' }} />
        <ModalBody>
          <Text fontWeight="bold" mb="0.5rem" fontSize="xl">
            {creditFacilityName}
          </Text>
          <Text fontSize="sm" color="gray.600" mb="1rem">
            {`This will be uploaded as Version ${fileVersion}`}
          </Text>
          <UploadCashBalanceForm
            organizationId={organizationId}
            creditFacilityId={creditFacilityId}
            reportingDate={artifact.reportingDate}
            fileVersion={fileVersion}
            accept={{
              'image/png': ['.png'],
            }}
            onClose={onClose}
            onSubmit={async (artifactInstanceId) => {
              // ******
              // deliverables only keeps track of inProgress records that are instantiated by the Deliverables UI
              // i.e. if cash balance form is submitted elsewhere, deliverables does not create a corresponding inProgress record
              // (it will just look for an completed record when BBPL eventually writes to `cash_response_tapes`)
              // ******
              finleyAssert(
                artifactInstanceId,
                '[Deliverables Cash Balances Feed Upload]: Artifact Instance Id for BBPL Initializing Record must exist as no file version is provided.',
              );
              await createDeliverableArtifactInstanceMutation({
                variables: {
                  deliverableArtifactId: artifact.id,
                  active: true,
                  status: 'inProgress',
                  rawDatabase: 'pg',
                  rawNamespace: 'borrowing_base_pipeline_artifact_instances',
                  rawIdentifier: commonUtils.generatePgIdentifier({
                    column: 'id',
                    id: artifactInstanceId,
                  }),
                },
              });
            }}
            onLoading={() => {}}
            onSuccess={() => {}}
          />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

type MonthlyServicerReportModalProps = {
  organizationId: string;
  creditFacilityId: string;
  creditAgreementId: string;
  artifact: DeliverableArtifactGQLType;
  isOpen: boolean;
  onClose: () => void;
  onGenerated?: (instanceId: string) => void; // pass in the instance id of the artifact instance that was generated
};
const MonthlyServicerReportModal = ({
  organizationId,
  creditFacilityId,
  creditAgreementId,
  artifact,
  isOpen,
  onClose,
  onGenerated,
}: MonthlyServicerReportModalProps) => {
  finleyAssert(
    artifact.config.__typename === 'MonthlyServicerReportConfig',
    `Deliverables: Cannot evaluate report for deliverable artifact type ${artifact.config.artifactType}. Must be monthlyServicerReport type.`,
  );

  const toast = useToast();
  const reportName = artifact.config.reportName;

  const [
    createDeliverableArtifactInstanceMutation,
  ] = useCreateDeliverableArtifactInstanceMutation({
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: GetDeliverableArtifactsByContainerIdDocumentGQLType,
        fetchPolicy: 'network-only',
        variables: { containerId: artifact.containerId },
      },
      {
        query: GetDeliverableContainerByIdDocumentGQLType,
        fetchPolicy: 'network-only',
        variables: { containerId: artifact.containerId },
      },
    ],
    onCompleted(data) {
      if (onGenerated) {
        onGenerated(data.createDeliverableArtifactInstance.id);
      }
    },
  });

  const [produceGenerateMessage] = useProduceReportingGenerateMessageMutation({
    fetchPolicy: 'network-only',
    async onCompleted(data) {
      try {
        // create corresponding deliverable artifact instance
        await createDeliverableArtifactInstanceMutation({
          variables: {
            deliverableArtifactId: artifact.id,
            active: true,
            status: 'inProgress',
            rawDatabase: 'pg',
            rawNamespace: 'reporting_generate_worklease_status',
            rawIdentifier: commonUtils.generatePgIdentifier({
              column: 'id',
              id: data.result.recordId,
            }),
          },
        });
        toast({
          description:
            'Successfully produced message to generate report. Please check your email in a few minutes.',
          duration: 3000,
          status: 'success',
          position: 'top',
        });
      } catch (err) {
        toast({
          status: 'error',
          position: 'top',
          duration: 5000,
          isClosable: true,
          title:
            commonTypeguards.isFinleyErrorType(err) ||
            commonTypeguards.isErrorType(err)
              ? err.message
              : 'Failed to produce message to generate report. Please contact Finley support.',
        });
      }
    },
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Generate Monthly Servicer Report</ModalHeader>
        <ModalCloseButton _focus={{ boxShadow: 'none' }} />
        <ModalBody>
          <Stack spacing="20px">
            <Text fontWeight="semibold" mb="1rem" fontSize="md">
              Are you sure you want to generate an MSR report?
            </Text>
            <Flex padding-top="5px" justifyContent="center" pb={3}>
              <Button
                mr="15px"
                variant="solid"
                colorScheme="aurora"
                _focus={{ boxShadow: 'none' }}
                onClick={() => {
                  produceGenerateMessage({
                    variables: {
                      worklease: {
                        initiatingOrganizationId: organizationId,
                        creditFacilityId,
                        creditAgreementId,
                        reportName,
                        reportingDate: artifact.reportingDate,
                        mode: 'Live',
                        reportTemplateFileType: 'xlsx',
                        reportConfigType: 'monthlyServicerReport',
                        config: null,
                      },
                      parameters: null,
                    },
                  });
                }}
              >
                Generate
              </Button>
              <Button
                color="aurora"
                _focus={{ boxShadow: 'none' }}
                onClick={() => onClose()}
              >
                Cancel
              </Button>
            </Flex>
          </Stack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

type PortCovProps = {
  type: 'portfolioCovenant';
  portCovs: CreditAgreement.DomainEntity.PortfolioCovenantProperty[];
};

type FinCovProps = {
  type: 'financialCovenant';
  finCovs: CreditAgreement.DomainEntity.FinancialCovenantProperty[];
};

type CovenantModalProps = {
  typeSpecificProps: PortCovProps | FinCovProps;
  creditFacilityId: string;
  creditAgreementId: string;
  artifact: DeliverableArtifactGQLType;
  isOpen: boolean;
  onClose: () => void;
  onGenerated?: (instanceId: string) => void; // pass in the instance id of the artifact instance that was generated
};
const CovenantModal = ({
  typeSpecificProps,
  creditFacilityId,
  creditAgreementId,
  artifact,
  isOpen,
  onClose,
  onGenerated,
}: CovenantModalProps) => {
  finleyAssert(
    artifact.config.__typename === 'PortfolioCovenantConfig' ||
      artifact.config.__typename === 'FinancialCovenantConfig',
    `Deliverables: Cannot evaluate port cov for deliverable artifact type ${artifact.config.artifactType}. Must be portfolioCovenant or financialCovenant type.`,
  );

  const toast = useToast();
  const covenantKey = artifact.config.covenantKey;
  const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const [
    createDeliverableArtifactInstanceMutation,
  ] = useCreateDeliverableArtifactInstanceMutation({
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: GetDeliverableArtifactsByContainerIdDocumentGQLType,
        fetchPolicy: 'network-only',
        variables: { containerId: artifact.containerId },
      },
      {
        query: GetDeliverableContainerByIdDocumentGQLType,
        fetchPolicy: 'network-only',
        variables: { containerId: artifact.containerId },
      },
    ],
  });

  const [
    produceFinCovMessageApi,
  ] = useProduceFinancialCovenantWorkleaseMessageMutation({
    onCompleted: () => {
      toast({
        title: 'Initiated Financial Covenants evaluation based on latest CA.',
        duration: 3000,
        status: 'success',
        position: 'top',
      });
    },
    onError: (error) => {
      toast({
        title: getErrorMessageFromApolloError({
          error,
          defaultMessage:
            'Unable to initiate Financial Covenants evaluation. Please contact technical assistance if this continues.',
        }),
        isClosable: true,
        status: 'error',
        position: 'top',
      });
    },
  });

  const onSubmitFinancialCovenant = useCallback(
    async (props: { triggerDate: Util.FullDateStr }) => {
      const { data } = await produceFinCovMessageApi({
        variables: {
          worklease: {
            covenantKeys: [covenantKey],
            creditAgreementId,
            creditFacilityId,
            triggerDate: props.triggerDate,
            mode: 'RESULT',
            // result mode means we expect to get back exactly one financial covenant id corresponding to the record for this covenantKey
            // (e.g. produceFinCovMessageApi should return `financialCovenantKeyToIdMap.length === 1`)
          },
        },
      });
      finleyAssert(
        data?.produceFinancialCovenantWorkleaseMessage,
        'Missing data on "produceFinCovMessageApi".',
      );
      finleyAssert(
        data.produceFinancialCovenantWorkleaseMessage
          .financialCovenantKeyToIdMap.length === 1,
        '[Deliverables] Exactly one financial covenant should be evaluated at a time.',
      );
      const {
        financialCovenantRecordId,
      } = data.produceFinancialCovenantWorkleaseMessage.financialCovenantKeyToIdMap[0];

      return financialCovenantRecordId;
    },
    [produceFinCovMessageApi, covenantKey, creditAgreementId, creditFacilityId],
  );

  const [evaluatePortfolioCovenants] = useEvaluatePortfolioCovenantsMutation({
    fetchPolicy: 'network-only',
    onCompleted: () => {
      toast({
        title:
          'Initiated portfolio covenants evaluation.  Please check your email in a few minutes.',
        duration: 3000,
        status: 'success',
        position: 'top',
      });
    },
    onError: (error) => {
      toast({
        title: getErrorMessageFromApolloError({
          error,
          defaultMessage:
            'Unable to initiate portfolio covenants evaluation. Please see technical assistance.',
        }),
        isClosable: true,
        status: 'error',
        position: 'top',
      });
    },
  });

  const onSubmitPortfolioCovenant = useCallback(
    async (props: { triggerDate: Util.FullDateStr }) => {
      const { data: evaluatePortCovData } = await evaluatePortfolioCovenants({
        variables: {
          evaluatePortfolioCovenantsRequest: {
            covenantKeys: [covenantKey],
            creditAgreementId,
            creditFacilityId,
            triggerDate: props.triggerDate,
            mode: 'RESULT',
            // result mode means we expect to get back exactly one portfolio covenant id corresponding to the record for this covenantKey
            // (e.g. evaluatePortfolioCovenants should return `portfolioCovenantIds.length === 1`)
          },
        },
      });
      finleyAssert(
        evaluatePortCovData?.evaluatePortfolioCovenants,
        'Missing data on "evaluatePortfolioCovenants".',
      );
      finleyAssert(
        evaluatePortCovData.evaluatePortfolioCovenants.portfolioCovenantIds
          .length === 1,
        '[Deliverables] Exactly one portfolio covenant should be evaluated at a time.',
      );
      const portfolioCovenantId =
        evaluatePortCovData.evaluatePortfolioCovenants.portfolioCovenantIds[0];

      return portfolioCovenantId;
    },
    [
      evaluatePortfolioCovenants,
      covenantKey,
      creditAgreementId,
      creditFacilityId,
    ],
  );

  const onSubmit = useCallback(
    async (props: { triggerDate: Util.FullDateStr }) => {
      let covenantId: string | undefined;
      let rawNamespace: DeliverableRawNamespaceGQLType | undefined;
      switch (typeSpecificProps.type) {
        case 'portfolioCovenant': {
          covenantId = await onSubmitPortfolioCovenant(props);
          rawNamespace = 'portfolio_covenants';
          break;
        }
        case 'financialCovenant': {
          covenantId = await onSubmitFinancialCovenant(props);
          rawNamespace = 'financial_covenants';
          break;
        }
        default: {
          finleyAssertUnreachable(typeSpecificProps);
        }
      }
      finleyAssert(
        covenantId && rawNamespace,
        'Expecting a covenant id and namespace for port cov OR fin cov evaluation.',
      );

      // create corresponding deliverable artifact instance
      const {
        data: deliverableArtifactInstanceData,
      } = await createDeliverableArtifactInstanceMutation({
        variables: {
          deliverableArtifactId: artifact.id,
          active: true,
          status: 'inProgress',
          rawDatabase: 'pg',
          rawNamespace,
          rawIdentifier: commonUtils.generatePgIdentifier({
            column: 'id',
            id: covenantId,
          }),
        },
      });
      finleyAssert(
        deliverableArtifactInstanceData?.createDeliverableArtifactInstance,
        'Missing data on "createDeliverableArtifactInstanceMutation".',
      );
      if (onGenerated) {
        onGenerated(
          deliverableArtifactInstanceData?.createDeliverableArtifactInstance.id,
        );
      }
      onClose();
    },
    [
      artifact.id,
      createDeliverableArtifactInstanceMutation,
      onGenerated,
      onSubmitFinancialCovenant,
      onSubmitPortfolioCovenant,
      typeSpecificProps,
      onClose,
    ],
  );

  let covenant:
    | CreditAgreement.DomainEntity.PortfolioCovenantProperty
    | CreditAgreement.DomainEntity.FinancialCovenantProperty
    | undefined;

  let intermediateFieldName:
    | 'portfolioCovenantReportingDate'
    | 'financialCovenantReportingDate'
    | undefined;
  switch (typeSpecificProps.type) {
    case 'portfolioCovenant': {
      covenant = typeSpecificProps.portCovs.find(
        (cov) => cov.covenantKey === covenantKey,
      );
      intermediateFieldName = 'portfolioCovenantReportingDate';
      break;
    }
    case 'financialCovenant': {
      covenant = typeSpecificProps.finCovs.find(
        (cov) => cov.covenantKey === covenantKey,
      );
      intermediateFieldName = 'financialCovenantReportingDate';
      break;
    }
    default: {
      finleyAssertUnreachable(typeSpecificProps);
    }
  }

  if (!covenant) {
    return (
      <ErrorModal
        text={`${_.startCase(
          typeSpecificProps.type,
        )} with covenant key ${covenantKey} is missing in current credit agreement.`}
        isOpen={isOpen}
        onClose={onClose}
      />
    );
  }
  finleyAssert(intermediateFieldName, 'Intermediate field name must be set.');

  const reportingDateField = covenant._config_?.intermediateFields.find(
    (f) => f.field === intermediateFieldName,
  );
  if (!reportingDateField) {
    return (
      <ErrorModal
        text={`${_.startCase(
          typeSpecificProps.type,
        )} with covenant key ${covenantKey} is missing '${intermediateFieldName}' config intermediate field.`}
        isOpen={isOpen}
        onClose={onClose}
      />
    );
  }
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{`Evaluate ${_.startCase(
          typeSpecificProps.type,
        )}`}</ModalHeader>
        <ModalCloseButton _focus={{ boxShadow: 'none' }} />
        <ModalBody>
          <Formik
            initialValues={{
              triggerDate: commonDate.dateToFullDateString(
                new Date(),
                clientTimeZone,
              ),
            }}
            onSubmit={(props) => {
              onSubmit(props);
            }}
          >
            {(formProps: FormikProps<any>) => (
              <Form onSubmit={formProps.handleSubmit}>
                <Stack spacing="20px">
                  <Flex alignItems="center">
                    <Text minW="25%">Reporting Date Formula:</Text>
                    <Spacer />
                    <Code>{reportingDateField.config.formula}</Code>
                  </Flex>

                  <Divider />

                  <Text fontWeight="semibold">
                    {`Pass in a triggerDate below such that reporting date formula evaluates to ${commonDate.fullDateStrToHumanDateString(
                      artifact.reportingDate,
                    )}.`}
                  </Text>
                  <Flex alignItems="center" width="30%">
                    <SimpleDatePicker
                      selectedDate={commonDate.fullDateStrToDate(
                        formProps.values.triggerDate,
                      )}
                      setSelectedDate={(date: Date | null) => {
                        if (!date) return;
                        const fullDateStr = commonDate.dateToFullDateString(
                          date,
                          clientTimeZone,
                        );
                        formProps.setFieldValue('triggerDate', fullDateStr);
                      }}
                    />
                  </Flex>

                  <Flex padding-top="5px" justifyContent="flex-end" pb={3}>
                    <Button
                      type="submit"
                      mr="15px"
                      variant="solid"
                      colorScheme="aurora"
                      _focus={{ boxShadow: 'none' }}
                    >
                      Evaluate
                    </Button>
                    <Button
                      color="aurora"
                      _focus={{ boxShadow: 'none' }}
                      onClick={() => onClose()}
                    >
                      Cancel
                    </Button>
                  </Flex>
                </Stack>
              </Form>
            )}
          </Formik>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

type OrgProvidedInputModalProps = {
  organizationId: string;
  artifact: DeliverableArtifactGQLType;
  isOpen: boolean;
  onClose: () => void;
  dataTape?: DataTapeState;
  onSubmitDataTape: (
    file: File,
    tape: DataTape,
    uploadReportingDate: Util.FullDateStr,
    fileVersion: number,
  ) => Promise<void>;
};
const OrgProvidedInputModal = ({
  organizationId,
  artifact,
  isOpen,
  onClose,
  dataTape,
  onSubmitDataTape,
}: OrgProvidedInputModalProps) => {
  const { user } = useUserAuthOrThrow();
  finleyAssert(
    artifact.config.__typename === 'OrgProvidedInputConfig',
    `Deliverables: Cannot upload Org Provided Value for deliverable artifact type ${artifact.config.artifactType}. Must be orgProvidedInput type.`,
  );

  const bgColor = useColorModeValue(colors.aurora['400'], 'white');
  const bgTextColor = useColorModeValue('white', 'black');

  const { data: orgProvidedInputsData } = useGetOrgProvidedInputByKeyQuery({
    variables: {
      organizationId,
      key: artifact.config.key,
    },
  });

  // Fetch pipeline runs to determine the file version for CSV upload
  const { data: pipelineRunsData } = useGetBorrowerPipelineRunsQuery({
    variables: {
      borrowerOrganizationId: user.organization.id,
      startDate: artifact.reportingDate,
      endDate: artifact.reportingDate,
    },
    fetchPolicy: 'network-only',
    skip: !dataTape, // Only fetch if we have a data tape (CSV upload path)
  });

  // Calculate fileVersion for CSV upload: find existing run for this date and increment, or default to 1
  const fileVersion = useMemo(() => {
    const existingRun = pipelineRunsData?.getPipelineRuns?.find(
      (run) => run.reportingDate === artifact.reportingDate,
    );
    return existingRun ? existingRun.fileVersion + 1 : 1;
  }, [pipelineRunsData, artifact.reportingDate]);
  const [
    createDeliverableArtifactInstanceMutation,
  ] = useCreateDeliverableArtifactInstanceMutation({
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: GetDeliverableArtifactsByContainerIdDocumentGQLType,
        fetchPolicy: 'network-only',
        variables: { containerId: artifact.containerId },
      },
      {
        query: GetDeliverableContainerByIdDocumentGQLType,
        fetchPolicy: 'network-only',
        variables: { containerId: artifact.containerId },
      },
    ],
  });

  if (!orgProvidedInputsData) {
    return <Skeleton />;
  }

  const orgProvidedInput = orgProvidedInputsData.getOrgProvidedInputByKey;

  const onSubmitPrimitive = async (orgProvidedValueId: string) => {
    // create corresponding deliverable artifact instance
    await createDeliverableArtifactInstanceMutation({
      variables: {
        deliverableArtifactId: artifact.id,
        active: true,
        status: 'success',
        completedDatabase: 'pg',
        completedNamespace: 'org_provided_values',
        completedIdentifier: commonUtils.generatePgIdentifier({
          column: 'id',
          id: orgProvidedValueId,
        }),
      },
    });

    onClose();
  };

  // if data tape doesn't exist, only support primitive upload
  if (!dataTape) {
    return (
      <ValueCreateModal
        isOpen={isOpen}
        onCancel={onClose}
        onSubmit={onSubmitPrimitive}
        orgProvidedInputs={[orgProvidedInput]}
        reportingDate={artifact.reportingDate}
      />
    );
  }

  // if data tape exists, allow for EITHER csv upload OR primitive upload
  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        onClose();
      }}
      size="3xl"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{`Create Org Provided Value (${getTitleCaseExternalFilename(
          dataTape.externalFilename,
        )})`}</ModalHeader>
        <ModalCloseButton _focus={{ boxShadow: 'none' }} />
        <ModalBody width="100%">
          <Tabs isFitted variant="unstyled">
            <TabList>
              <Tab
                marginTop={0}
                marginRight={0}
                height="40px"
                borderWidth="thin"
                borderRadius="8px 0px 0px 8px"
                _selected={{
                  color: bgTextColor,
                  bg: bgColor,
                }}
                _focus={{ boxShadow: 'none' }}
              >
                Value
              </Tab>
              <Tab
                marginTop={0}
                marginRight={0}
                height="40px"
                borderWidth="thin"
                borderRadius="0px 8px 8px 0px"
                _selected={{
                  color: bgTextColor,
                  bg: bgColor,
                }}
                _focus={{ boxShadow: 'none' }}
              >
                CSV File
              </Tab>
            </TabList>

            <TabPanels>
              {/* Value */}
              <TabPanel px="0px">
                <ValueCreateForm
                  orgProvidedInputs={[orgProvidedInput]}
                  reportingDate={artifact.reportingDate}
                  onCancel={onClose}
                  onSubmit={onSubmitPrimitive}
                />
              </TabPanel>

              {/* CSV */}
              <TabPanel px="0px">
                <Text fontSize="sm" color="gray.600" mb="1rem">
                  {`This will be uploaded as Version ${fileVersion}`}
                </Text>
                <UploadDataTapeForm
                  dataTape={dataTape}
                  allowedExtensions={[dataTape.fileType]}
                  onClose={onClose}
                  handleFile={(file) => {
                    onSubmitDataTape(
                      file,
                      dataTape,
                      artifact.reportingDate,
                      fileVersion,
                    );
                  }}
                />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

// Exports
export const Upload = ({
  creditFacilityName,
  creditFacilityId,
  organizationId,
  artifact,
  isOpen,
  onClose,
  dataTape,
  onSubmitDataTape,
  defaultToNewVersion,
}: {
  creditFacilityName: string;
  creditFacilityId: string;
  organizationId: string;
  artifact: DeliverableArtifactGQLType;
  isOpen: boolean;
  onClose: () => void;
  // below two params are only for manual uploader file uploads (loan tape, repayment tape, and supplemental tape)
  // will be ignored for other deliverable artifact types
  dataTape?: DataTapeState;
  onSubmitDataTape: (
    file: File,
    tape: DataTape,
    uploadReportingDate: Util.FullDateStr,
    fileVersion: number,
  ) => Promise<void>;
  /** If true, default to new version; otherwise default to highest available existing version */
  defaultToNewVersion: boolean;
}) => {
  const toast = useToast();

  const {
    loading: creditAgreementLoading,
    data: creditAgreementData,
  } = useGetCurrentCreditAgreementFullArtifactQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: {
      creditFacilityId,
    },
    fetchPolicy: 'no-cache',
  });

  if (creditAgreementLoading) {
    return <Skeleton />;
  }

  const creditAgreement = parseCreditAgreementFullArtifact(
    creditAgreementData?.currentCreditAgreementFullArtifact?.creditAgreement,
  );

  if (!creditAgreement) {
    return (
      <ErrorModal
        text="A current Credit Agreement must exist to upload Deliverables."
        isOpen={isOpen}
        onClose={onClose}
      />
    );
  }

  const {
    id: creditAgreementId,
    portfolioCovenants,
    financialCovenants,
  } = creditAgreement;

  switch (artifact.config.artifactType) {
    case 'loanTape':
    case 'repaymentTape':
    case 'supplementalTape':
    case 'financialStatement':
      return (
        <DataTapeModal
          artifact={artifact}
          isOpen={isOpen}
          onClose={onClose}
          dataTape={dataTape}
          onSubmitDataTape={onSubmitDataTape}
          defaultToNewVersion={defaultToNewVersion}
        />
      );
    case 'cashBalancesFeed':
      return (
        <CashBalancesFeedModal
          creditFacilityName={creditFacilityName}
          creditFacilityId={creditFacilityId}
          organizationId={organizationId}
          artifact={artifact}
          isOpen={isOpen}
          onClose={onClose}
        />
      );
    case 'monthlyServicerReportXlsx':
      return (
        <MonthlyServicerReportModal
          organizationId={organizationId}
          creditFacilityId={creditFacilityId}
          creditAgreementId={creditAgreementId}
          artifact={artifact}
          isOpen={isOpen}
          onClose={onClose}
        />
      );
    case 'portfolioCovenant':
      return (
        <CovenantModal
          typeSpecificProps={{
            type: 'portfolioCovenant',
            portCovs: portfolioCovenants,
          }}
          creditFacilityId={creditFacilityId}
          creditAgreementId={creditAgreementId}
          artifact={artifact}
          isOpen={isOpen}
          onClose={onClose}
        />
      );
    case 'financialCovenant':
      return (
        <CovenantModal
          typeSpecificProps={{
            type: 'financialCovenant',
            finCovs: financialCovenants,
          }}
          creditFacilityId={creditFacilityId}
          creditAgreementId={creditAgreementId}
          artifact={artifact}
          isOpen={isOpen}
          onClose={onClose}
        />
      );

    case 'orgProvidedInput':
      return (
        <OrgProvidedInputModal
          organizationId={organizationId}
          artifact={artifact}
          isOpen={isOpen}
          onClose={onClose}
          dataTape={dataTape}
          onSubmitDataTape={onSubmitDataTape}
        />
      );
    case 'monthlyStatement':
      throw new Error('Monthly Statement upload not allowed');
    default:
      return finleyAssertUnreachable(artifact.config.artifactType);
  }
};
